import React from 'react'

import { ChatEditStateReducer, initialState, Actions } from './reducer'

import { putPatch } from '../../lib/api'
import { useEnvironment } from '../EnvironmentProvider'
import { useQueueState } from '../QueueStateProvider'
import { useSocket } from '../SocketProvider'
import { transformToEmoji } from '../../utilities/stringToEmoji'
import { useAppDispatch } from '../../hooks/redux'
import { AddErrorAlert } from '../../slices/alert'

const ChatEditStateContext = React.createContext(null)

const findLatestMessage = (user, convo) => {
  const messages = convo?.messages ?? []

  const isPeer = convo && convo.session === convo.user?.id

  const filtered = messages.filter(({ session_id, type, senderFull }) => {
    const chatMatch = type === 'chat' && senderFull?.id === user?.id

    if (isPeer) {
      return chatMatch
    }

    return chatMatch && session_id === convo?.session
  })

  if (!filtered.length) {
    return null
  }

  const sorted = filtered.sort((a, b) => new Date(b.date) - new Date(a.date))

  return sorted[0]
}

const findMessage = (convo, id) => {
  const messages = convo?.messages ?? []

  if (!messages.length) {
    return null
  }

  const found = messages.find(f => f.id === id)

  return found
}

const ChatEditStateProvider = ({ children }) => {
  const reduxDispatch = useAppDispatch()
  const { user } = useEnvironment()
  const { conversation } = useQueueState()
  const {
    api: { handlePatch }
  } = useSocket()

  const [state, dispatch] = React.useReducer(ChatEditStateReducer, initialState)

  const start = React.useCallback(() => {
    const latest = findLatestMessage(user, conversation)

    if (latest) {
      dispatch({ type: Actions.START, originalMessage: latest })
    }
    return latest
  }, [user, conversation])

  const edit = React.useCallback(
    id => {
      const found = findMessage(conversation, id)

      if (found) {
        dispatch({ type: Actions.START, originalMessage: found, isOpen: true })
      }
    },
    [conversation]
  )

  const quote = React.useCallback(message => {
    dispatch({ type: Actions.QUOTE, message })
  }, [])

  const end = React.useCallback(() => {
    dispatch({ type: Actions.RESET })
  }, [])

  const update = React.useCallback(message => {
    dispatch({ type: Actions.EDIT, message })
  }, [])

  const submit = React.useCallback(async () => {
    const { originalMessage, newMessage } = state
    const { id, content: startingMessage, tags = [] } = originalMessage

    const { newText } = transformToEmoji(newMessage)

    if (startingMessage !== newText) {
      const data = {
        content: { body: newText.trim() },
        tags,
        action: 'chat'
      }
      try {
        const res = await putPatch(id, data)
        handlePatch(res)
      } catch (err) {
        console.error('Error putting patch', err)
        reduxDispatch(AddErrorAlert({ err }))
      }
    }
    end()
  }, [state, end, handlePatch, reduxDispatch])

  return (
    <ChatEditStateContext.Provider
      value={{ state, start, end, update, submit, edit, quote }}
    >
      {children}
    </ChatEditStateContext.Provider>
  )
}

export default ChatEditStateProvider

export const useChatEditState = () => React.useContext(ChatEditStateContext)
